import makeStyles from "@material-ui/styles/makeStyles";
import React, { useMemo } from "react";
import {
    IBackgroundImage,
    IThreediumCustomization,
    ILocation,
    IPlanar2dModel,
    OverlayAttributeType,
} from "../../../constants/product-customizations/customization";
import { IOverlay2dProps, Overlay2d } from "./overlay2d";
import { DATATEST } from "../../../constants/integration";
import { BackgroundImage2d } from "./background-image-2d";
import _, { Dictionary } from "lodash";
import { CustomizationSelectionType } from "constants/product-customizations/customization-selection.types";

export interface ICustomizer2dProps {
    customizations: IThreediumCustomization[];
    selectedVisualizerId?: string;
    productModel?: IPlanar2dModel;
}

const useStyles = makeStyles({
    сustomizerContainer: {
        position: "relative",
        height: "100%",
        width: "100%",
    },
    previewContainer: {
        position: "relative",
        height: "100%",
        width: "100%",
    },
    overlay: {
        position: "absolute",
        border: "1px solid black",
        borderStyle: "dotted",
    },
});

interface IRenderData {
    visualizerAttributeIds: Set<string>;
    overlays: IOverlay2dProps[];
    backgroundImageUrl?: string;
}

type LookupTable<T> = Dictionary<T | undefined>;

const upsert = <T,>(lookupTable: LookupTable<T>, key: string, fallback?: () => T) =>
    (lookupTable[key] = lookupTable[key] ?? fallback?.());

type NestedLookupTable<T> = LookupTable<LookupTable<T>>;

const upsertNested = <T,>(
    lookupTable: NestedLookupTable<T>,
    key: string,
    innerKey: string,
    fallback?: () => LookupTable<T>,
    innerFallback?: () => T
) => {
    const innerMap = upsert(lookupTable, key, () => fallback?.() ?? {});
    if (!innerMap) {
        return;
    }

    return upsert(innerMap, innerKey, innerFallback);
};

export const Customizer2d: React.FC<ICustomizer2dProps> = ({ customizations, selectedVisualizerId, productModel }) => {
    const classes = useStyles();

    const renderMap = useMemo(() => {
        const visualizerSelectorMap: NestedLookupTable<IBackgroundImage> = {};
        const visualizerLocationMap: NestedLookupTable<ILocation> = {};

        //we group all by backgroundSelector to have all overlays on one background selected
        return (customizations || []).reduce((acc, customization) => {
            const {
                planar2d: { backgroundImageSelector, overlay } = {},
                binding: { rotation } = {},
                selection,
                visualizerAttribute,
                visualizer: { _id: id } = {},
            } = customization;

            const { locations = [], backgroundImages = [] } = productModel || customization?.planarData || {};

            if (!overlay || !id || !backgroundImageSelector || !selection) {
                return acc;
            }

            const backgroundImage = upsertNested(visualizerSelectorMap, id, backgroundImageSelector, () =>
                _.keyBy(backgroundImages, "selector")
            );
            const location = upsertNested(visualizerLocationMap, id, overlay.locationId, () =>
                _.keyBy(locations, "_id")
            );

            const backgroundGroup = upsert(acc, backgroundImageSelector, () => ({
                overlays: [] as IOverlay2dProps[],
                backgroundImageUrl: backgroundImage?.image?.url,
                visualizerAttributeIds: new Set(),
            }));

            if (!backgroundGroup) {
                return acc;
            }

            const type = overlay.type;
            const overlays = backgroundGroup.overlays;
            const overlayProps = <Type, Selection>(type: Type, selection: Selection) => ({
                type,
                location,
                selection,
                rotation,
            });

            if (type === OverlayAttributeType.IMAGE && selection.type === CustomizationSelectionType.PRINTED_IMAGE) {
                overlays.push(overlayProps(type, selection));
            }

            if (type === OverlayAttributeType.TEXT && selection.type === CustomizationSelectionType.PRINTED_TEXT) {
                overlays.push(overlayProps(type, selection));
            }

            backgroundGroup.visualizerAttributeIds.add(visualizerAttribute?._id ?? "");

            return acc;
        }, {}) as LookupTable<IRenderData>;
    }, [customizations, productModel]);

    const activeBackgroundGroup = useMemo(
        () => Object.values(renderMap).find((data) => data?.visualizerAttributeIds.has(selectedVisualizerId ?? "")),
        [renderMap, selectedVisualizerId]
    );

    if (!activeBackgroundGroup || !selectedVisualizerId) {
        return null;
    }

    const { backgroundImageUrl, overlays } = activeBackgroundGroup;

    return (
        <div className={classes.сustomizerContainer}>
            <BackgroundImage2d url={backgroundImageUrl}>
                {overlays.map((overlay, index) => (
                    <span data-test={`${DATATEST.CUSTOMIZATIONS.CUSTOMIZATIONS_2D_OVERLAY}-${index}`} key={index}>
                        <Overlay2d {...overlay} />
                    </span>
                ))}
            </BackgroundImage2d>
        </div>
    );
};
